import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Star } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { usePoints } from '../../context/PointsContext';
import { API } from '../../utils/api';

const TopHeader = ({ title, showBack = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { points } = usePoints();
  const [unreadCount, setUnreadCount] = useState(0);
  const [org, setOrg] = useState(null);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications?limit=50`);
      const unread = response.data.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchOrg = async () => {
    try {
      const response = await axios.get(`${API}/organizations/current`);
      setOrg(response.data);
    } catch (error) {
      // silent fallback to default branding
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    fetchOrg();
  }, []);

  const orgLogo = org?.logo_url || '/logo-symbol.png';
  const orgPrimary = org?.primary_color;

  // Expose primary color as a CSS variable so other components can pick it up.
  useEffect(() => {
    if (orgPrimary) {
      document.documentElement.style.setProperty('--org-primary', orgPrimary);
    }
  }, [orgPrimary]);

  return (
    <motion.div 
      className="top-header"
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      {/* Left: Logo (clickable) */}
      <motion.div 
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        transition={{ duration: 0.3 }}
      >
        {showBack && (
          <motion.button 
            className="header-back" 
            onClick={() => navigate(-1)} 
            data-testid="header-back" 
            style={{ marginRight: 8 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft size={24} />
          </motion.button>
        )}
        <motion.div
          onClick={user?.org_id === 'demo-contest-2026' ? undefined : () => navigate('/organization')}
          style={{ cursor: user?.org_id === 'demo-contest-2026' ? 'default' : 'pointer' }}
          whileHover={user?.org_id === 'demo-contest-2026' ? undefined : { scale: 1.05 }}
          whileTap={user?.org_id === 'demo-contest-2026' ? undefined : { scale: 0.95 }}
        >
          {org == null ? (
            // Placeholder while /organizations/current is in-flight. Keeps
            // the header width stable and avoids briefly showing the wrong
            // (default) logo before the real org logo resolves. Sized to
            // match the final container per-variant.
            <div
              data-testid="header-org-logo-placeholder"
              style={{
                width: user?.org_id === 'demo-contest-2026' ? 160 : 32,
                height: user?.org_id === 'demo-contest-2026' ? 45 : 32,
                background: 'transparent',
              }}
            />
          ) : user?.org_id === 'demo-contest-2026' ? (
            <img
              src={orgLogo}
              alt={org?.name || 'socialstar'}
              data-testid="header-org-logo-demo"
              style={{ width: 160, height: 45, borderRadius: 0, objectFit: 'contain' }}
            />
          ) : (
            <img
              src={orgLogo}
              alt={org?.name || 'socialstar'}
              style={{ width: 32, height: 32, borderRadius: orgLogo === '/logo-symbol.png' ? 0 : '50%', objectFit: 'cover' }}
            />
          )}
        </motion.div>
      </motion.div>

      {/* Center: Org name (clickable, routes to /organization). Uses
          flex:1 to occupy the space between the left logo and the right
          controls, then truncates with ellipsis so a long org name never
          pushes the notifications bell off the header on small screens. */}
      <motion.div
        onClick={() => navigate('/organization')}
        data-testid="header-org-name"
        style={{
          flex: 1,
          minWidth: 0,
          margin: '0 12px',
          textAlign: 'left',
          fontSize: window.innerWidth < 768 ? 14 : 16,
          fontWeight: 600,
          color: orgPrimary || 'var(--text-dark)',
          cursor: org?.name ? 'pointer' : 'default',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontFamily: 'Inter, sans-serif',
          letterSpacing: '0.2px',
        }}
        whileHover={org?.name ? { scale: 1.02 } : undefined}
        whileTap={org?.name ? { scale: 0.98 } : undefined}
      >
        {org?.name || ''}
      </motion.div>

      {/* Right: Points, Notifications and Menu */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: window.innerWidth < 768 ? 6 : 8
      }}>
        {/* Points Display */}
        <motion.div
          onClick={() => navigate('/rewards')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: window.innerWidth < 768 ? 3 : 6,
            padding: window.innerWidth < 768 ? '4px 8px' : '6px 12px',
            background: orgPrimary
              ? `${orgPrimary}1A` // ~10% opacity tint of the org color
              : 'linear-gradient(135deg, #FFF7ED, #FEF3C7)',
            borderRadius: 20,
            cursor: 'pointer',
            border: orgPrimary ? `1px solid ${orgPrimary}66` : '1px solid #FCD34D'
          }}
          whileHover={{ scale: 1.05, boxShadow: orgPrimary ? `0 4px 12px ${orgPrimary}4D` : '0 4px 12px rgba(251, 191, 36, 0.3)' }}
          whileTap={{ scale: 0.98 }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <motion.div
            animate={{ rotate: [0, -10, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          >
            <Star size={window.innerWidth < 768 ? 16 : 18} fill={orgPrimary || '#F59E0B'} color={orgPrimary || '#F59E0B'} />
          </motion.div>
          <span style={{ 
            fontSize: window.innerWidth < 768 ? 12 : 14,
            fontWeight: 700, 
            color: orgPrimary || '#92400E',
            fontFamily: 'Inter, sans-serif'
          }}>
            {points.toLocaleString()}
          </span>
        </motion.div>

        {/* Notifications */}
        <motion.button 
          onClick={() => navigate('/notifications')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: window.innerWidth < 768 ? 4 : 8,
            color: 'var(--text-dark)',
            position: 'relative'
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          data-testid="notifications-btn"
        >
          <svg width={window.innerWidth < 768 ? 20 : 24} height={window.innerWidth < 768 ? 20 : 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
          <AnimatePresence>
            {unreadCount > 0 && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  position: 'absolute',
                  top: window.innerWidth < 768 ? 2 : 6,
                  right: window.innerWidth < 768 ? 2 : 6,
                  width: unreadCount > 9 ? 18 : 12,
                  height: 12,
                  borderRadius: 6,
                  background: '#DC2626',
                  color: 'white',
                  fontSize: 9,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white'
                }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </motion.div>
  );
};

export default TopHeader;
